import { createAsyncThunk } from '@reduxjs/toolkit';
import { tr } from '../../i18n/i18n';
import i18n from '../../i18n/i18n';
import {
  ExamDetailsResponse,
  ExamByIdDetailsResponse,
  CreateExamRequest,
  CreateExamResponse,
  ExamPayload,
  UpdateExamResponse,
  ExamSubjectDetailsResponse,
  PublishExamResponse,
  CompleteExamResponse,
  CancelExamResponse,
  DeleteExamResponse,
  IExamAcknowledgementParams,
  IExamAcknowledgementResponse,
  StartMarkingExamSubjectResponse,
  PatchExamSubjectMarksRequest,
  PatchExamSubjectMarksResponse,
  CompleteMarkingExamSubjectResponse,
} from '../../interface/examDetails.interface';
import {
  CloneExamPaperResponse,
  FetchExamPaperByIdResponse,
  FetchExamPaperResponse,
  FetchReusablePapersParams,
  FetchReusablePapersResponse,
  UpsertExamPaperRequest,
  UpsertExamPaperResponse,
} from '../../interface/examPaper.interface';
import { IErrorResponse } from '../../interface/main.interface';
import { IExamReportCardResponse } from '../../interface/reportCard.interface';
import {
  GetAttemptForMarkingResponse,
  PatchExamItemMarkRequest,
  PatchExamItemMarkResponse,
  PatchExamAttemptMarksRequest,
  PatchExamAttemptMarksResponse,
  ReleaseAttemptMarksResponse,
} from '../../interface/examMarking.interface';
import {
  addExam,
  cloneExamPaper,
  examCancel,
  examDelete,
  examPublish,
  examRelease,
  getExamAcknowledgements,
  getExamById,
  getExamReportCard,
  getExamPaper,
  getExamPaperById,
  getExams,
  getExamSubjectById,
  getAttemptForMarking,
  getReusableExamPapers,
  updateExam,
  upsertExamPaper,
  startMarkingExamSubject,
  patchExamSubjectMarks,
  patchExamItemMark,
  patchExamAttemptMarks,
  releaseAttemptMarks,
  completeMarkingExamSubject,
} from '../../services/examManagement/exam.service';
import { normalizeAxiosError } from '../../utility/axiosErrorHandler';

// Type for error handling in async thunks
interface ApiError {
  status?: number;
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
  };
  message?: string;
}

export const fetchExams = createAsyncThunk<
  ExamDetailsResponse,
  ExamPayload | undefined,
  { rejectValue: IErrorResponse }
>('exam/fetch', async (params, { rejectWithValue }) => {
  try {
    const response = await getExams(params);
    return response;
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      status: error?.status || error?.response?.status || 500,
      message:
        error?.message ||
        error?.response?.data?.message ||
        i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

export const fetchExamById = createAsyncThunk<
  ExamByIdDetailsResponse,
  string,
  { rejectValue: IErrorResponse }
>('exam/fetch-by-id', async (id, { rejectWithValue }) => {
  try {
    const response = await getExamById(id);
    return response;
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      status: error?.status || error?.response?.status || 500,
      message:
        error?.message ||
        error?.response?.data?.message ||
        i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

export const createExam = createAsyncThunk<
  CreateExamResponse,
  CreateExamRequest,
  { rejectValue: IErrorResponse }
>('exam/create', async (payload, { rejectWithValue }) => {
  try {
    const response = await addExam(payload);
    return response;
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      message:
        error?.message ||
        error?.response?.data?.message ||
        i18n.t('toast.failed_to_create_exam', 'Failed to create exam.'),
    });
  }
});

export const updateExamAction = createAsyncThunk<
  UpdateExamResponse,
  { examId: string; body: CreateExamRequest },
  { rejectValue: IErrorResponse }
>('exam/update', async ({ examId, body }, { rejectWithValue }) => {
  try {
    const response = await updateExam(examId, body);
    return response;
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      message:
        error?.message ||
        error?.response?.data?.message ||
        tr('g.failed_to_update_exam', 'Failed to update exam.'),
    });
  }
});

export const publishExam = createAsyncThunk<
  PublishExamResponse,
  string,
  { rejectValue: IErrorResponse }
>('exam/publish', async (examId, { rejectWithValue }) => {
  try {
    const response = await examPublish(examId);
    return response;
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      message:
        error?.message ||
        error?.response?.data?.message ||
        i18n.t('toast.failed_to_publish_exam', 'Failed to publish exam.'),
    });
  }
});

export const releaseExam = createAsyncThunk<
  CompleteExamResponse,
  string,
  { rejectValue: IErrorResponse }
>('exam/release', async (examId, { rejectWithValue }) => {
  try {
    const response = await examRelease(examId);
    return response;
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      message:
        error?.message ||
        error?.response?.data?.message ||
        i18n.t('toast.failed_to_release_exam', 'Failed to release exam.'),
    });
  }
});

export const cancelExam = createAsyncThunk<
  CancelExamResponse,
  string,
  { rejectValue: IErrorResponse }
>('exam/cancel', async (examId, { rejectWithValue }) => {
  try {
    const response = await examCancel(examId);
    return response;
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      message:
        error?.message ||
        error?.response?.data?.message ||
        i18n.t('toast.failed_to_cancel_exam', 'Failed to cancel exam.'),
    });
  }
});

export const deleteExam = createAsyncThunk<
  DeleteExamResponse,
  string,
  { rejectValue: IErrorResponse }
>('exam/delete', async (examId, { rejectWithValue }) => {
  try {
    const response = await examDelete(examId);
    return response;
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      message:
        error?.message ||
        error?.response?.data?.message ||
        tr('fe_scorecard.failed_to_delete_exam', 'Failed to delete exam.'),
    });
  }
});

export const fetchExamSubjectById = createAsyncThunk<
  ExamSubjectDetailsResponse,
  string,
  { rejectValue: IErrorResponse }
>('exam/fetch-subject-by-id', async (id, { rejectWithValue }) => {
  try {
    const response = await getExamSubjectById(id);
    return response;
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      status: error?.status || error?.response?.status || 500,
      message:
        error?.message ||
        error?.response?.data?.message ||
        i18n.t('toast.failed_to_fetch_exam_subject', 'Failed to fetch exam subject.'),
    });
  }
});

/* --------------------------- Marking (grading) --------------------------- *
 * The axios interceptor rejects with the BACKEND BODY, not an AxiosError, so
 * `error.message` here is already the server's localized message. It is
 * surfaced verbatim; the t() default only covers the case where the body has
 * no message at all (e.g. a network failure).
 * ------------------------------------------------------------------------- */

export const startMarkingExamSubjectAction = createAsyncThunk<
  StartMarkingExamSubjectResponse,
  string,
  { rejectValue: IErrorResponse }
>('exam/start-marking', async (examSubjectId, { rejectWithValue }) => {
  try {
    return await startMarkingExamSubject(examSubjectId);
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      status: error?.status || error?.response?.status || 500,
      message:
        error?.message ||
        error?.response?.data?.message ||
        i18n.t('exam_marking.err_start_marking', 'Failed to start marking.'),
    });
  }
});

export const updateExamSubjectMarks = createAsyncThunk<
  PatchExamSubjectMarksResponse,
  { examSubjectId: string; body: PatchExamSubjectMarksRequest },
  { rejectValue: IErrorResponse }
>('exam/update-marks', async ({ examSubjectId, body }, { rejectWithValue }) => {
  try {
    return await patchExamSubjectMarks(examSubjectId, body);
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      status: error?.status || error?.response?.status || 500,
      message:
        error?.message ||
        error?.response?.data?.message ||
        i18n.t('exam_marking.err_update_marks', 'Failed to update marks.'),
    });
  }
});

/**
 * CVA-4197 plan 0011 slice 4 (Flow A canvas) — read-only. Row-level
 * authorization is stricter than `fetchExamSubjectById`'s: an invigilator who
 * can already reach the roster is still refused here (403), matching the
 * roster-blind treatment they already get.
 */
export const fetchAttemptForMarking = createAsyncThunk<
  GetAttemptForMarkingResponse,
  { examSubjectId: string; studentId: string },
  { rejectValue: IErrorResponse }
>('exam/fetch-attempt-for-marking', async ({ examSubjectId, studentId }, { rejectWithValue }) => {
  try {
    return await getAttemptForMarking(examSubjectId, studentId);
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      status: error?.status || error?.response?.status || 500,
      message:
        error?.message ||
        error?.response?.data?.message ||
        i18n.t('exam_marking.err_load_attempt', "Failed to load this student's attempt."),
    });
  }
});

/**
 * CVA-4197 plan 0011 slice 5 — per-question Save Draft on the Flow A canvas.
 * A genuinely separate call from `updateExamAttemptMarks` below — clicking
 * one card's Save Draft must never also fire the rail's bulk save.
 */
export const updateExamItemMark = createAsyncThunk<
  PatchExamItemMarkResponse,
  { examSubjectId: string; studentId: string; itemId: string; body: PatchExamItemMarkRequest },
  { rejectValue: IErrorResponse }
>(
  'exam/update-item-mark',
  async ({ examSubjectId, studentId, itemId, body }, { rejectWithValue }) => {
    try {
      return await patchExamItemMark(examSubjectId, studentId, itemId, body);
    } catch (err: unknown) {
      const error = err as ApiError;
      return rejectWithValue({
        status: error?.status || error?.response?.status || 500,
        message:
          error?.message ||
          error?.response?.data?.message ||
          i18n.t('exam_marking.err_save_item', 'Failed to save this question.'),
      });
    }
  },
);

/**
 * CVA-4197 plan 0011 slice 5 — the rail's own bulk "Save Draft", saving every
 * scorable item's current draft in one call. Never flips `exam_marks.status`
 * and is safely repeatable any number of times.
 */
export const updateExamAttemptMarks = createAsyncThunk<
  PatchExamAttemptMarksResponse,
  { examSubjectId: string; studentId: string; body: PatchExamAttemptMarksRequest },
  { rejectValue: IErrorResponse }
>('exam/update-attempt-marks', async ({ examSubjectId, studentId, body }, { rejectWithValue }) => {
  try {
    return await patchExamAttemptMarks(examSubjectId, studentId, body);
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      status: error?.status || error?.response?.status || 500,
      message:
        error?.message ||
        error?.response?.data?.message ||
        i18n.t('exam_marking.err_bulk_save_draft', 'Failed to save draft.'),
    });
  }
});

/**
 * CVA-4197 plan 0011 slice 6 — Release Score for one student's attempt. On
 * success the caller (the canvas) refetches the whole attempt payload via
 * `fetchAttemptForMarking` rather than patching state here — release can
 * silently zero-fill an unknown number of items server-side, so a full
 * refetch is the only way to reflect the true persisted state. This thunk
 * therefore has no corresponding `.fulfilled` reducer case in the slice.
 */
export const releaseAttemptMarksAction = createAsyncThunk<
  ReleaseAttemptMarksResponse,
  { examSubjectId: string; studentId: string },
  { rejectValue: IErrorResponse }
>('exam/release-attempt-marks', async ({ examSubjectId, studentId }, { rejectWithValue }) => {
  try {
    return await releaseAttemptMarks(examSubjectId, studentId);
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      status: error?.status || error?.response?.status || 500,
      message:
        error?.message ||
        error?.response?.data?.message ||
        i18n.t('exam_marking.err_release_score', 'Failed to release this score.'),
    });
  }
});

export const completeMarkingExamSubjectAction = createAsyncThunk<
  CompleteMarkingExamSubjectResponse,
  string,
  { rejectValue: IErrorResponse }
>('exam/complete-marking', async (examSubjectId, { rejectWithValue }) => {
  try {
    return await completeMarkingExamSubject(examSubjectId);
  } catch (err: unknown) {
    // Must preserve `data` (not just `message`) — the PENDING_RELEASE
    // blocking case (CVA-exam-marking-submitted-absent) hands back the list
    // of students still awaiting release so the UI can name them, not just
    // report a generic failure.
    return rejectWithValue(
      normalizeAxiosError(
        err,
        i18n.t('exam_marking.err_complete_marking', 'Failed to complete marking.'),
      ),
    );
  }
});

export const fetchExamAcknowledgements = createAsyncThunk<
  IExamAcknowledgementResponse,
  { examId: string; params?: IExamAcknowledgementParams },
  { rejectValue: IErrorResponse }
>('exam/fetch-acknowledgements', async ({ examId, params }, { rejectWithValue }) => {
  try {
    const response = await getExamAcknowledgements(examId, params);
    return response;
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      status: error?.status || error?.response?.status || 500,
      message:
        error?.message ||
        error?.response?.data?.message ||
        i18n.t('toast.failed_to_fetch_acknowledgements', 'Failed to fetch acknowledgements.'),
    });
  }
});

/**
 * Staff view of a student's single-exam report card.
 * The axios interceptor rejects with the backend body, so the machine-readable
 * `errorCode` is read straight off the error and preserved for the page (a
 * still-being-marked exam answers 400 + REPORT_CARD_NOT_RELEASED).
 */
export const fetchExamReportCard = createAsyncThunk<
  IExamReportCardResponse,
  { examId: string; studentId: string },
  { rejectValue: IErrorResponse & { errorCode?: string } }
>('exam/fetch-report-card', async ({ examId, studentId }, { rejectWithValue }) => {
  try {
    return await getExamReportCard(examId, studentId);
  } catch (err: unknown) {
    return rejectWithValue({
      ...normalizeAxiosError(
        err,
        tr('toast.failed_to_fetch_the_report_card', 'Failed to fetch the report card.'),
      ),
      errorCode: (err as { errorCode?: string })?.errorCode,
    });
  }
});

// Exam Paper (authoring) thunks. Kept in this file (not a separate
// `examPaper.action.ts`) since the paper-builder feature is part of the
// existing exam domain, not a standalone one — mirrors the backend, which
// folds exam-paper authoring into the existing exam-subject module rather
// than a new module.

export const fetchReusableExamPapers = createAsyncThunk<
  FetchReusablePapersResponse,
  FetchReusablePapersParams,
  { rejectValue: IErrorResponse }
>('exam/fetch-reusable-papers', async (params, { rejectWithValue }) => {
  try {
    const response = await getReusableExamPapers(params);
    return response;
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      status: error?.status || error?.response?.status || 500,
      message:
        error?.message ||
        error?.response?.data?.message ||
        tr('exam_paper.error_fetch_reusable_papers', 'Failed to fetch reusable papers.'),
    });
  }
});

export const fetchExamPaper = createAsyncThunk<
  FetchExamPaperResponse,
  string,
  { rejectValue: IErrorResponse }
>('exam/fetch-paper', async (examSubjectId, { rejectWithValue }) => {
  try {
    const response = await getExamPaper(examSubjectId);
    return response;
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      status: error?.status || error?.response?.status || 500,
      message:
        error?.message ||
        error?.response?.data?.message ||
        tr('exam_paper.error_fetch_paper', 'Failed to fetch exam paper.'),
    });
  }
});

export const upsertExamPaperAction = createAsyncThunk<
  UpsertExamPaperResponse,
  { examSubjectId: string; body: UpsertExamPaperRequest },
  { rejectValue: IErrorResponse }
>('exam/upsert-paper', async ({ examSubjectId, body }, { rejectWithValue }) => {
  try {
    const response = await upsertExamPaper(examSubjectId, body);
    return response;
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      message:
        error?.message ||
        error?.response?.data?.message ||
        tr('exam_paper.error_save_paper', 'Failed to save exam paper.'),
    });
  }
});

export const cloneExamPaperAction = createAsyncThunk<
  CloneExamPaperResponse,
  { examSubjectId: string; sourcePaperId: string },
  { rejectValue: IErrorResponse }
>('exam/clone-paper', async ({ examSubjectId, sourcePaperId }, { rejectWithValue }) => {
  try {
    const response = await cloneExamPaper(examSubjectId, sourcePaperId);
    return response;
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      message:
        error?.message ||
        error?.response?.data?.message ||
        tr('exam_paper.error_clone_paper', 'Failed to clone exam paper.'),
    });
  }
});

/**
 * Fetches a paper by its own id — used by `PaperSourceChoice` to hydrate a
 * chosen "Select an Existing Template" paper into a local editable draft
 * (via `mapItemViewToCardDraft`) without persisting anything server-side.
 * Replaces the old `cloneExamPaperAction`-on-select flow, which required a
 * real `examSubjectId` that doesn't exist yet during atomic create/edit.
 */
export const fetchExamPaperById = createAsyncThunk<
  FetchExamPaperByIdResponse,
  string,
  { rejectValue: IErrorResponse }
>('exam/fetch-paper-by-id', async (paperId, { rejectWithValue }) => {
  try {
    const response = await getExamPaperById(paperId);
    return response;
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      status: error?.status || error?.response?.status || 500,
      message:
        error?.message ||
        error?.response?.data?.message ||
        tr('exam_paper.error_fetch_selected_paper', 'Failed to fetch the selected paper.'),
    });
  }
});

export const examActions = {
  fetchExams,
  fetchExamReportCard,
  fetchExamById,
  createExam,
  updateExamAction,
  fetchExamSubjectById,
  publishExam,
  releaseExam,
  cancelExam,
  deleteExam,
  fetchExamAcknowledgements,
  fetchReusableExamPapers,
  fetchExamPaper,
  fetchExamPaperById,
  upsertExamPaperAction,
  cloneExamPaperAction,
  startMarkingExamSubjectAction,
  updateExamSubjectMarks,
  completeMarkingExamSubjectAction,
  fetchAttemptForMarking,
  updateExamItemMark,
  updateExamAttemptMarks,
  releaseAttemptMarksAction,
};
